import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Paper,
  InputAdornment,
  IconButton
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { getFriendlyAuthError } from "../utils/friendlyAuthError";

export default function LoginPage() {
  const { login, sendResetPasswordEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(location.state?.message || null);
  const [actionMessage, setActionMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setActionMessage(null);
    try {
      await login(form.email, form.password);
      navigate("/profile");
    } catch (err) {
      setError(getFriendlyAuthError(err.message));
    }
  };

  const handlePasswordReset = async () => {
    if (!form.email) {
      setError("Enter your email first, then request a password reset.");
      return;
    }

    setError(null);
    setActionMessage(null);
    setSendingReset(true);
    try {
      await sendResetPasswordEmail(form.email);
      setActionMessage("Password reset email sent.");
    } catch (err) {
      setError(getFriendlyAuthError(err.message));
    }
    setSendingReset(false);
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fafafa",
        m: 0,
        p: 0,
        py: { xs: 2, sm: 4, md: 6 },
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 1, sm: 2 } }}>
        <Paper elevation={4} sx={{ p: { xs: 2, sm: 4, md: 5 }, borderRadius: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <LockOutlinedIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: "primary.main", mb: 1 }} />
            <Typography variant="h4" mb={2} sx={{ fontSize: { xs: 22, sm: 28 } }}>Sign In</Typography>
          </Box>
          <form onSubmit={handleSubmit}>
            {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
            <TextField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon />
                  </InputAdornment>
                )
              }}
              sx={{ fontSize: { xs: 14, sm: 16 } }}
            />
            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ fontSize: { xs: 14, sm: 16 } }}
            />
            {actionMessage && <Alert severity="success" sx={{ mt: 2 }}>{actionMessage}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ mt: 3, mb: 1, fontSize: { xs: 15, sm: 17 } }}
            >
              Login
            </Button>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                color="secondary"
                onClick={handlePasswordReset}
                disabled={sendingReset}
                sx={{ textTransform: "none", px: 0, minWidth: "auto" }}
              >
                Forgot password?
              </Button>
            </Box>
            <Button
              variant="text"
              onClick={() => navigate("/register")}
              sx={{ mt: 2, fontSize: { xs: 14, sm: 16 }, textTransform: "none" }}
            >
              Don't have an account? Register
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}